import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { GuardianMark } from "../../brand/marks";
import { OdingardMark } from "../../brand/OdingardLogo";
import {
  GUARDIAN_APP,
  GUARDIAN_AUDIENCES,
  GUARDIAN_BOUNDARY,
  GUARDIAN_CATALOG,
  GUARDIAN_CONTACT,
  GUARDIAN_DEFINITION,
  GUARDIAN_DEMOS,
  GUARDIAN_DOCTRINE,
  GUARDIAN_EXAMPLES,
  GUARDIAN_HERO,
  GUARDIAN_KEYS,
  GUARDIAN_LIFECYCLE,
  GUARDIAN_OS,
  GUARDIAN_PACKAGES,
  GUARDIAN_PAGES,
  GUARDIAN_PRICING,
  GUARDIAN_PROOF_NOTE,
  GUARDIAN_QUICKSTART,
  GUARDIAN_SDK_REPO,
  GUARDIAN_STATUS,
  GUARDIAN_STORY,
  GUARDIAN_VERIFY,
  MATURITY,
  SECTOR_LABEL,
  TIER,
  type Demo,
  type GuardianPageDef,
  type Tier,
} from "../../content/guardian-site";
import { AuthoritySeal, MaturityTag, Tabs } from "./seal";
import "./guardian.css";

function Cta({ href, children, kind = "primary" }: { href: string; children: ReactNode; kind?: "primary" | "ghost" }) {
  const className = `gd-btn gd-btn--${kind}`;
  if (href.startsWith("http") || href.startsWith("#")) return <a className={className} href={href}>{children}</a>;
  return <Link className={className} to={href}>{children}</Link>;
}

function SubNav() {
  const here = useLocation().pathname.replace(/\/+$/, "") || "/";
  return (
    <nav className="gd-subnav" aria-label="Guardian">
      <Link to="/guardian" className="gd-subnav__brand">
        <GuardianMark className="gd-subnav__mark" />
        <b>Guardian</b>
      </Link>
      <ul>
        {GUARDIAN_PAGES.map((p) => (
          <li key={p.key}>
            <Link to={p.path} aria-current={here === p.path ? "page" : undefined}>{p.navLabel}</Link>
          </li>
        ))}
      </ul>
      <Cta href={GUARDIAN_APP}>Get Guardian</Cta>
    </nav>
  );
}

function Head({ eyebrow, heading, lede, id, center }: { eyebrow: string; heading: string; lede?: string; id: string; center?: boolean }) {
  return (
    <header className={`gd-head${center ? " gd-head--center" : ""}`}>
      <p className="gd-eyebrow">{eyebrow}</p>
      <h2 id={id} className="gd-serif">{heading}</h2>
      {lede ? <p className="gd-lede">{lede}</p> : null}
    </header>
  );
}

const AUDIENCE_ICON: Record<string, string> = { people: "People", organizations: "Organizations", developers: "Developers", platforms: "Platforms" };

function Hero() {
  const h = GUARDIAN_HERO;
  return (
    <section className="gd-band gd-band--vault gd-hero" aria-labelledby="gd-hero">
      <div className="gd-wrap">
        <div className="gd-hero__card">
          <p className="gd-hero__brand"><GuardianMark className="gd-hero__mark" /> {h.brand}</p>
          <p className="gd-eyebrow">{h.category}</p>
          <h1 id="gd-hero" className="gd-hero__title">{h.headline}</h1>
          <p className="gd-hero__subhead">{h.subhead}</p>
          <p className="gd-lede">{h.body}</p>
          <div className="gd-ctas">
            <Cta href={h.primary.href}>{h.primary.label}</Cta>
            <Cta href={h.secondary.href} kind="ghost">{h.secondary.label}</Cta>
          </div>
          <ul className="gd-hero__paths" aria-label="Start as">
            {GUARDIAN_AUDIENCES.filter((a) => a.key !== "platforms").map((a) => (
              <li key={a.key}><a href={`#audience-${a.key}`}>{AUDIENCE_ICON[a.key]}</a></li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Lifecycle() {
  const [active, setActive] = useState(GUARDIAN_LIFECYCLE[0]?.key ?? "establish");
  return (
    <section className="gd-band gd-band--deep" aria-labelledby="gd-lifecycle">
      <div className="gd-wrap">
        <Head eyebrow="The authority lifecycle" id="gd-lifecycle" heading="Authority, from the moment it is given to the moment it ends" lede="Choose a stage. ALLOW, ASK and DENY are how Guardian answers inside “Govern execution” — the product is the whole system of authority around them." />
        <Tabs
          className="gd-flow"
          label="Guardian authority lifecycle"
          items={GUARDIAN_LIFECYCLE}
          active={active}
          onSelect={setActive}
          renderTab={(s) => <><span className="gd-mono">{String(GUARDIAN_LIFECYCLE.indexOf(s) + 1).padStart(2, "0")}</span>{s.name}</>}
          renderPanel={(s) => (
            <div className="gd-flow__panel">
              <div className="gd-explorer__head"><h3 className="gd-serif">{s.name}</h3><MaturityTag status={s.maturity} /></div>
              <p className="gd-lede">{s.body}</p>
              <p className="gd-serif gd-flow__example">{s.example}</p>
            </div>
          )}
        />
      </div>
    </section>
  );
}

function Story() {
  const s = GUARDIAN_STORY;
  return (
    <section className="gd-band gd-band--paper" aria-labelledby="gd-story">
      <div className="gd-wrap gd-story">
        <Head eyebrow="Why Guardian" id="gd-story" heading={s.question} />
        <div>
          {s.paragraphs.map((p) => <p key={p.slice(0, 24)} className="gd-lede">{p}</p>)}
          <p className="gd-serif gd-story__close">{s.close}</p>
          <p className="gd-definition">{GUARDIAN_DEFINITION}</p>
        </div>
      </div>
    </section>
  );
}

function Audiences() {
  return (
    <section className="gd-band gd-band--vault" id="get-started" aria-labelledby="gd-audiences">
      <div className="gd-wrap">
        <Head eyebrow="Get started" id="gd-audiences" heading="One authority system. Start where you are." />
        <ul className="gd-paths gd-paths--4">
          {GUARDIAN_AUDIENCES.map((a) => (
            <li key={a.key} id={`audience-${a.key}`}>
              <p className="gd-eyebrow">{a.name}</p>
              <h3 className="gd-serif">{a.line}</h3>
              <p>{a.body}</p>
              <div className="gd-ctas"><Cta href={a.cta.href}>{a.cta.label}</Cta></div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function DemoCard({ d, open }: { d: Demo; open?: boolean }) {
  return (
    <details className="gd-demo-card" open={open}>
      <summary>
        <span className="gd-demo-card__domain gd-mono">{d.domain}</span>
        <b className="gd-serif">{d.title}</b>
        <MaturityTag status={d.maturity} label={d.label} />
      </summary>
      <p>{d.story}</p>
      <ol className="gd-demo-card__steps">{d.steps.map((s) => <li key={s}>{s}</li>)}</ol>
      {d.note ? <p className="gd-note gd-demo-card__note">{d.note}</p> : null}
      <p className="gd-mono gd-demo-card__receipts">{d.receipts.length ? `Receipts: ${d.receipts.join(" · ")}` : `Evidence: ${d.evidence ?? ""}`}</p>
    </details>
  );
}

function ProofTeaser() {
  return (
    <section className="gd-band gd-band--deep" aria-labelledby="gd-proof-teaser">
      <div className="gd-wrap">
        <Head eyebrow="See Guardian in action" id="gd-proof-teaser" heading="Real authority paths, labeled for exactly what they prove" lede={GUARDIAN_PROOF_NOTE} />
        <div className="gd-demo-grid">{GUARDIAN_DEMOS.slice(0, 3).map((d) => <DemoCard key={d.key} d={d} />)}</div>
        <p><Link className="gd-link" to="/guardian/proof">Every demonstration and receipt verification →</Link></p>
      </div>
    </section>
  );
}

function Pricing() {
  const p = GUARDIAN_PRICING;
  return (
    <section className="gd-band gd-band--paper" aria-labelledby="gd-pricing">
      <div className="gd-wrap">
        <Head eyebrow="Plans" id="gd-pricing" heading="Start free. Build on Developer. Scale with Business." lede={p.note} />
        <ul className="gd-plans">
          {p.plans.map((plan) => (
            <li key={plan.name}>
              <h3 className="gd-serif">{plan.name}</h3>
              <p className="gd-plans__price">{plan.price}</p>
              {plan.alt ? <p className="gd-plans__alt">{plan.alt}</p> : null}
              {plan.available === true ? <MaturityTag status="deployed" label="Available now" /> : plan.available === false ? <MaturityTag status="planned" label="Approved price · not yet on sale" /> : null}
              <p>{plan.detail}</p>
              {plan.cta.href.startsWith("http") ? <a className="gd-link" href={plan.cta.href}>{plan.cta.label} →</a> : <Link className="gd-link" to={plan.cta.href}>{plan.cta.label} →</Link>}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Boundary() {
  return (
    <section className="gd-band gd-band--deep" aria-labelledby="gd-boundary">
      <div className="gd-wrap">
        <div className="gd-limits">
          <h2 id="gd-boundary" className="gd-serif">Where Guardian’s protection ends</h2>
          <ul>{GUARDIAN_BOUNDARY.map((l) => <li key={l}>{l}</li>)}</ul>
        </div>
        <p className="gd-ctas"><Cta href={GUARDIAN_STATUS} kind="ghost">Live service status</Cta></p>
      </div>
    </section>
  );
}

function Close() {
  return (
    <section className="gd-band gd-band--vault gd-close" aria-labelledby="gd-close">
      <div className="gd-wrap gd-head--center">
        <h2 id="gd-close" className="gd-serif gd-close__line">{GUARDIAN_DOCTRINE}</h2>
        <div className="gd-ctas gd-ctas--center">
          <Cta href={GUARDIAN_APP}>Get Guardian</Cta>
          <Cta href="/guardian/developers" kind="ghost">Install the SDK</Cta>
          <Cta href={GUARDIAN_CONTACT.pilot} kind="ghost">Request a pilot</Cta>
          <Cta href={GUARDIAN_CONTACT.embedded} kind="ghost">Explore integration</Cta>
        </div>
        <p className="gd-company"><OdingardMark /> Odingard Security · Trust infrastructure for autonomous systems</p>
      </div>
    </section>
  );
}

function PageTitle({ page }: { page: GuardianPageDef }) {
  return (
    <section className="gd-band gd-band--vault gd-pagetitle">
      <div className="gd-wrap">
        <p className="gd-eyebrow">Guardian · {page.navLabel}</p>
        <h1 className="gd-serif">{page.navLabel === "Authority OS" ? "The Authority OS" : page.navLabel}</h1>
        <p className="gd-lede">{page.lede}</p>
      </div>
    </section>
  );
}

function Overview() {
  return <><Hero /><Lifecycle /><Story /><Audiences /><ProofTeaser /><Pricing /><Boundary /></>;
}

function AuthorityOs() {
  return (
    <>
      <Lifecycle />
      <section className="gd-band gd-band--paper" aria-labelledby="gd-os">
        <div className="gd-wrap">
          <Head eyebrow="Inside the Authority OS" id="gd-os" heading="More than an approval: a complete system of authority" lede="Each capability carries one status. Open it for what it does and where the evidence lives." />
          <ul className="gd-os">
            {GUARDIAN_OS.map((c) => (
              <li key={c.name}>
                <details>
                  <summary><b>{c.name}</b><MaturityTag status={c.maturity} /></summary>
                  <p>{c.body}</p>
                  <p className="gd-mono gd-os__evidence">Evidence: {c.evidence}</p>
                </details>
              </li>
            ))}
          </ul>
          <dl className="gd-legend-list">
            {(["deployed", "proven", "sandbox", "simulated"] as const).map((m) => <div key={m}><dt><MaturityTag status={m} /></dt><dd>{MATURITY[m].meaning}</dd></div>)}
          </dl>
        </div>
      </section>
      <section className="gd-band gd-band--vault" aria-labelledby="gd-seals">
        <div className="gd-wrap">
          <Head eyebrow="Inside “Govern execution”" id="gd-seals" heading="Three answers, decided deterministically" lede="A model may propose an action. It never decides whether it is allowed." />
          <ul className="gd-seals">
            <li><AuthoritySeal verdict="allow" /><p>Inside the authority given. Runs once, exactly as described.</p></li>
            <li><AuthoritySeal verdict="ask" /><p>Beyond the limits. Waits for the right human, bound to the exact action.</p></li>
            <li><AuthoritySeal verdict="deny" /><p>Unauthorized, changed, replayed, revoked or unclear. Never runs.</p></li>
          </ul>
        </div>
      </section>
      <Boundary />
    </>
  );
}

function Proof() {
  return (
    <>
      <section className="gd-band gd-band--deep" aria-labelledby="gd-demos">
        <div className="gd-wrap">
          <Head eyebrow="Proof suite · every run passed" id="gd-demos" heading="Money, production, machines, data — one authority system" lede={GUARDIAN_PROOF_NOTE} />
          <div className="gd-demo-grid">{GUARDIAN_DEMOS.map((d, i) => <DemoCard key={d.key} d={d} open={i === 0} />)}</div>
        </div>
      </section>
      <section className="gd-band gd-band--vault" aria-labelledby="gd-verify">
        <div className="gd-wrap">
          <Head eyebrow="Verify it yourself" id="gd-verify" heading="Every execution leaves a receipt anyone can check" lede="Receipts are Ed25519-signed. Guardian publishes its verification keys; the SDK verifies a receipt offline, without an account, and rejects it if a single byte changes." />
          <div className="gd-ctas">
            <Cta href={GUARDIAN_VERIFY}>Verify a receipt</Cta>
            <Cta href={GUARDIAN_KEYS} kind="ghost">Published keys</Cta>
            <Cta href={`${GUARDIAN_SDK_REPO}/blob/main/examples/verify-receipt.mjs`} kind="ghost">Offline verifier example</Cta>
          </div>
        </div>
      </section>
    </>
  );
}

const TIERS: Tier[] = ["production-integrated", "governed", "integration-ready"];

function Coverage() {
  const all = GUARDIAN_CATALOG.verticals;
  const [sector, setSector] = useState("all");
  const [tier, setTier] = useState<"all" | Tier>("all");
  const [q, setQ] = useState("");
  const shown = useMemo(
    () => all.filter((v) => (sector === "all" || v.sector === sector) && (tier === "all" || v.tier === tier) && (!q || `${v.title} ${v.description}`.toLowerCase().includes(q.toLowerCase()))),
    [all, sector, tier, q],
  );
  return (
    <section className="gd-band gd-band--paper" aria-labelledby="gd-coverage">
      <div className="gd-wrap">
        <Head eyebrow={`${all.length} verticals · declared coverage, not ${all.length} integrations`} id="gd-coverage" heading="Where the authority model reaches" lede="Generated from Guardian’s canonical catalog. A vertical’s tier says how much of it is integrated today." />
        <ul className="gd-tiers">
          {TIERS.map((t) => <li key={t}><b>{all.filter((v) => v.tier === t).length}</b> <span className={`gd-tier gd-tier--${t}`}>{TIER[t].label}</span><p>{TIER[t].meaning}</p></li>)}
        </ul>
        <div className="gd-filters">
          <label>Search<input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Banking, robots, CRM…" /></label>
          <label>Sector<select value={sector} onChange={(e) => setSector(e.target.value)}><option value="all">All sectors</option>{Object.entries(SECTOR_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
          <label>Tier<select value={tier} onChange={(e) => setTier(e.target.value as "all" | Tier)}><option value="all">All tiers</option>{TIERS.map((t) => <option key={t} value={t}>{TIER[t].label}</option>)}</select></label>
        </div>
        <p className="gd-note" aria-live="polite">{shown.length} of {all.length} verticals</p>
        <ul className="gd-verticals">
          {shown.map((v) => (
            <li key={v.id}>
              <details>
                <summary><b>{v.title}</b><span className="gd-mono">{SECTOR_LABEL[v.sector] ?? v.sector}</span><span className={`gd-tier gd-tier--${v.tier}`}>{TIER[v.tier].label}</span></summary>
                <p>{v.description}</p>
                <p className="gd-mono">{v.actions.join(" · ")}</p>
              </details>
            </li>
          ))}
        </ul>
        <p className="gd-note">Integration ready means Guardian governs the decision; the connector to the external system still has to be built for a deployment.</p>
      </div>
    </section>
  );
}

function Developers() {
  return (
    <>
      <section className="gd-band gd-band--paper" aria-labelledby="gd-pkgs">
        <div className="gd-wrap">
          <Head eyebrow="Open source · Apache-2.0" id="gd-pkgs" heading="Five packages on npm" lede="Your agent proposes; Guardian decides, executes with a single-use capability and returns a signed receipt." />
          <ul className="gd-pkgs">
            {GUARDIAN_PACKAGES.map((p) => (
              <li key={p.name}><a className="gd-mono gd-link" href={`https://www.npmjs.com/package/${p.name}`}>{p.name}</a><span className="gd-mono">v{p.version}</span><p>{p.use}</p></li>
            ))}
          </ul>
        </div>
      </section>
      <section className="gd-band gd-band--vault" aria-labelledby="gd-quick">
        <div className="gd-wrap">
          <Head eyebrow="Quickstart" id="gd-quick" heading="Your first governed action" />
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- scrollable code must be keyboard-reachable (axe scrollable-region-focusable) */}
          <pre className="gd-code" tabIndex={0} aria-label="Guardian SDK quickstart">{GUARDIAN_QUICKSTART}</pre>
          <div className="gd-ctas">
            <Cta href={GUARDIAN_APP}>Create a free account</Cta>
            <Cta href={GUARDIAN_SDK_REPO} kind="ghost">SDK on GitHub</Cta>
            <Cta href={`${GUARDIAN_SDK_REPO}/blob/main/packages/sdk/README.md`} kind="ghost">Full guide</Cta>
          </div>
        </div>
      </section>
      <section className="gd-band gd-band--deep" aria-labelledby="gd-examples">
        <div className="gd-wrap">
          <Head eyebrow="Runnable examples" id="gd-examples" heading="Run it before you build on it" />
          <ul className="gd-security">
            {GUARDIAN_EXAMPLES.map((e) => <li key={e.file}><a className="gd-mono gd-link" href={`${GUARDIAN_SDK_REPO}/blob/main/examples/${e.file}`}>{e.file}</a><p>{e.body}</p></li>)}
          </ul>
          <p className="gd-ctas"><Cta href={GUARDIAN_CONTACT.embedded} kind="ghost">Building an AI platform? Explore integration</Cta></p>
        </div>
      </section>
    </>
  );
}

const BODIES: Record<GuardianPageDef["key"], () => ReactNode> = {
  overview: Overview,
  "authority-os": AuthorityOs,
  proof: Proof,
  coverage: Coverage,
  developers: Developers,
};

export function GuardianPage({ page }: { page: GuardianPageDef }) {
  useEffect(() => {
    document.title = `${page.title} — Odingard`;
  }, [page]);
  const Body = BODIES[page.key];
  return (
    <main className="page guardian gd" id="main">
      <SubNav />
      {page.key === "overview" ? null : <PageTitle page={page} />}
      <Body />
      <Close />
    </main>
  );
}
